/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-12-15
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Alert,
} from '@mui/material';
import {
  useCreateStoreMutation,
  useUpdateStoreMutation,
  Store,
} from '../../../api/shoppingListApi';

interface StoreDialogProps {
  open: boolean;
  onClose: () => void;
  store?: Store | null;
  onSuccess?: () => void;
}

/**
 * Dialog for creating or editing a store.
 */
const StoreDialog: React.FC<StoreDialogProps> = ({
  open,
  onClose,
  store,
  onSuccess,
}) => {
  const [name, setName] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  const [createStore, { isLoading: isCreating }] = useCreateStoreMutation();
  const [updateStore, { isLoading: isUpdating }] = useUpdateStoreMutation();

  const isEditing = !!store;
  const isLoading = isCreating || isUpdating;

  const handleClose = () => {
    setName('');
    setNotes('');
    setError(null);
    onClose();
  };

  // Reset form when dialog opens/closes or store changes
  useEffect(() => {
    if (open) {
      setName(store?.name || '');
      setNotes(store?.notes || '');
      setError(null);
    }
  }, [open, store]);

  const handleSubmit = async () => {
    if (!name.trim()) {
      setError('Store name is required');
      return;
    }

    try {
      if (isEditing && store) {
        await updateStore({
          storeId: store.id,
          data: { name: name.trim(), notes: notes.trim() },
        }).unwrap();
      } else {
        await createStore({
          name: name.trim(),
          notes: notes.trim(),
        }).unwrap();
      }

      if (onSuccess) {
        onSuccess();
      }
      handleClose();
    } catch (err: any) {
      setError(err?.data?.detail || 'Failed to save store. Please try again.');
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          m: { xs: 1, sm: 2 },
          width: { xs: 'calc(100% - 16px)', sm: '100%' },
        },
      }}
    >
      <DialogTitle
        sx={{
          pb: { xs: 1, sm: 2 },
          fontSize: { xs: '1.125rem', sm: '1.25rem' },
        }}
      >
        {isEditing ? 'Edit Store' : 'Add New Store'}
      </DialogTitle>
      <DialogContent sx={{ px: { xs: 2, sm: 3 }, pt: { xs: 1, sm: 2 } }}>
        <Box sx={{ pt: { xs: 0.5, sm: 1 } }}>
          {error && (
            <Alert
              severity="error"
              sx={{
                mb: { xs: 1, sm: 2 },
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
              }}
            >
              {error}
            </Alert>
          )}

          <TextField
            label="Store Name"
            fullWidth
            value={name}
            onChange={e => setName(e.target.value)}
            margin="normal"
            required
            autoFocus
            disabled={isLoading}
            sx={{
              '& .MuiInputLabel-root': {
                fontSize: { xs: '0.875rem', sm: '1rem' },
              },
              '& .MuiInputBase-root': {
                fontSize: { xs: '0.875rem', sm: '1rem' },
              },
            }}
          />

          <TextField
            label="Notes (Optional)"
            fullWidth
            multiline
            rows={3}
            value={notes}
            onChange={e => setNotes(e.target.value)}
            margin="normal"
            disabled={isLoading}
            placeholder="Add any notes about this store..."
            sx={{
              '& .MuiInputLabel-root': {
                fontSize: { xs: '0.875rem', sm: '1rem' },
              },
              '& .MuiInputBase-root': {
                fontSize: { xs: '0.875rem', sm: '1rem' },
              },
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 1.5, sm: 2 } }}>
        <Button
          onClick={handleClose}
          disabled={isLoading}
          sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isLoading || !name.trim()}
          sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
        >
          {(() => {
            if (isLoading) return 'Saving...';
            if (isEditing) return 'Update';
            return 'Create';
          })()}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StoreDialog;
